import React, { useState } from 'react';
import { Plus, MapPin, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { createLocation } from '../../services/locationsApi';
import Spinner from '../Spinner/Spinner';
import './AddLocation.css';

const initialState = {
    name: '',
    zone: '',
    code: '',
    priority: 1,
    order: 1,
    centerCoordinates: { lat: '', lng: '' },
    radiusMeters: 1000,
    serviceCategoriesCount: '{}',
    serviceProviders: 0,
    productsCategoriesCount: '{}',
    productsProviders: 0,
};

const AddLocation = ({ onAdded } = {}) => {
    const [location, setLocation] = useState(initialState);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setLocation((prev) => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: value },
            }));
        } else {
            setLocation((prev) => ({
                ...prev,
                [field]: ['priority', 'order', 'radiusMeters', 'serviceProviders', 'productsProviders'].includes(field)
                    ? parseInt(value, 10) || 0
                    : value,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const {
            name, zone, code, priority, order,
            centerCoordinates, radiusMeters,
            serviceCategoriesCount, serviceProviders,
            productsCategoriesCount, productsProviders,
        } = location;

        if (!name || !zone || !code) {
            setError('Los campos "Nombre", "Zona" y "Código" son obligatorios.');
            return;
        }

        const lat = parseFloat(centerCoordinates.lat);
        const lng = parseFloat(centerCoordinates.lng);
        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            setError('Las coordenadas del centro son obligatorias y deben ser números válidos.');
            return;
        }
        if (lat < -90 || lat > 90) {
            setError('La latitud debe estar entre -90 y 90.');
            return;
        }
        if (lng < -180 || lng > 180) {
            setError('La longitud debe estar entre -180 y 180.');
            return;
        }

        let parsedServiceCategoriesCount;
        let parsedProductsCategoriesCount;
        try {
            parsedServiceCategoriesCount = serviceCategoriesCount ? JSON.parse(serviceCategoriesCount) : {};
        } catch (_) {
            setError('Formato JSON inválido en "Service Categories Count". Usar: {"categoría": cantidad}');
            return;
        }
        try {
            parsedProductsCategoriesCount = productsCategoriesCount ? JSON.parse(productsCategoriesCount) : {};
        } catch (_) {
            setError('Formato JSON inválido en "Products Categories Count". Usar: {"categoría": cantidad}');
            return;
        }

        const newLocation = {
            name: name.trim(),
            zone: zone.trim(),
            code: code.trim().toUpperCase(),
            priority,
            order,
            centerCoordinates: { lat, lng },
            radiusMeters,
            serviceCategoriesCount: parsedServiceCategoriesCount,
            serviceProviders,
            productsCategoriesCount: parsedProductsCategoriesCount,
            productsProviders,
        };

        setLoading(true);
        try {
            await createLocation(newLocation);
            toast.success('Location creada exitosamente');
            setLocation(initialState);
            setShowAdvanced(false);
            if (typeof onAdded === 'function') onAdded();
        } catch (err) {
            console.error('Error creating location:', err);
            setError('Error al crear la location. Verificá que no exista otra con el mismo nombre o código.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cme-addloc">
            <header className="cme-addloc__header">
                <div className="cme-addloc__title-icon"><MapPin size={20} /></div>
                <div>
                    <h2 className="cme-addloc__title">Agregar location</h2>
                    <p className="cme-addloc__subtitle">
                        Definí una zona geográfica con su centro, radio de cobertura y prioridad.
                    </p>
                </div>
            </header>

            {error && (
                <div className="cme-addloc__alert" role="alert">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            <form className="cme-addloc__form" onSubmit={handleSubmit}>
                {loading && <Spinner />}

                <section className="cme-addloc__section">
                    <h3 className="cme-addloc__section-title">Datos básicos</h3>
                    <div className="cme-addloc__row cme-addloc__row--3">
                        <div className="cme-addloc__field">
                            <label className="cme-addloc__label" htmlFor="cme-addloc-name">
                                Nombre <span className="cme-addloc__required">*</span>
                            </label>
                            <input
                                id="cme-addloc-name"
                                type="text"
                                className="cme-addloc__input"
                                value={location.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="Ej: Villa Elisa"
                                required
                            />
                        </div>
                        <div className="cme-addloc__field">
                            <label className="cme-addloc__label" htmlFor="cme-addloc-zone">
                                Zona <span className="cme-addloc__required">*</span>
                            </label>
                            <input
                                id="cme-addloc-zone"
                                type="text"
                                className="cme-addloc__input"
                                value={location.zone}
                                onChange={(e) => handleChange('zone', e.target.value)}
                                placeholder="Ej: La Plata"
                                required
                            />
                        </div>
                        <div className="cme-addloc__field">
                            <label className="cme-addloc__label" htmlFor="cme-addloc-code">
                                Código <span className="cme-addloc__required">*</span>
                            </label>
                            <input
                                id="cme-addloc-code"
                                type="text"
                                className="cme-addloc__input"
                                value={location.code}
                                onChange={(e) => handleChange('code', e.target.value)}
                                placeholder="Ej: VE"
                                maxLength={5}
                                required
                            />
                        </div>
                    </div>
                </section>

                <section className="cme-addloc__section">
                    <h3 className="cme-addloc__section-title">
                        Coordenadas del centro <span className="cme-addloc__required">*</span>
                    </h3>
                    <div className="cme-addloc__row cme-addloc__row--3">
                        <div className="cme-addloc__field">
                            <label className="cme-addloc__label" htmlFor="cme-addloc-lat">Latitud</label>
                            <input
                                id="cme-addloc-lat"
                                type="number"
                                step="any"
                                className="cme-addloc__input"
                                value={location.centerCoordinates.lat}
                                onChange={(e) => handleChange('centerCoordinates.lat', e.target.value)}
                                placeholder="-34.854"
                                required
                            />
                        </div>
                        <div className="cme-addloc__field">
                            <label className="cme-addloc__label" htmlFor="cme-addloc-lng">Longitud</label>
                            <input
                                id="cme-addloc-lng"
                                type="number"
                                step="any"
                                className="cme-addloc__input"
                                value={location.centerCoordinates.lng}
                                onChange={(e) => handleChange('centerCoordinates.lng', e.target.value)}
                                placeholder="-58.0863"
                                required
                            />
                        </div>
                        <div className="cme-addloc__field">
                            <label className="cme-addloc__label" htmlFor="cme-addloc-radius">Radio (m)</label>
                            <input
                                id="cme-addloc-radius"
                                type="number"
                                className="cme-addloc__input"
                                value={location.radiusMeters}
                                onChange={(e) => handleChange('radiusMeters', e.target.value)}
                                placeholder="1770"
                                min="100"
                                required
                            />
                        </div>
                    </div>
                </section>

                <section className="cme-addloc__section">
                    <h3 className="cme-addloc__section-title">Visualización</h3>
                    <div className="cme-addloc__row">
                        <div className="cme-addloc__field">
                            <label className="cme-addloc__label" htmlFor="cme-addloc-priority">Prioridad</label>
                            <input
                                id="cme-addloc-priority"
                                type="number"
                                className="cme-addloc__input"
                                value={location.priority}
                                onChange={(e) => handleChange('priority', e.target.value)}
                                min="1"
                                max="10"
                            />
                            <span className="cme-addloc__hint">1 a 10 — mayor número, mayor prioridad para resolver superposiciones.</span>
                        </div>
                        <div className="cme-addloc__field">
                            <label className="cme-addloc__label" htmlFor="cme-addloc-order">Orden</label>
                            <input
                                id="cme-addloc-order"
                                type="number"
                                className="cme-addloc__input"
                                value={location.order}
                                onChange={(e) => handleChange('order', e.target.value)}
                                min="1"
                            />
                            <span className="cme-addloc__hint">Menor número aparece primero en la lista.</span>
                        </div>
                    </div>
                </section>

                <button
                    type="button"
                    className="cme-addloc__advanced-toggle"
                    onClick={() => setShowAdvanced((s) => !s)}
                    aria-expanded={showAdvanced}
                >
                    {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    <span>{showAdvanced ? 'Ocultar campos avanzados' : 'Mostrar campos avanzados (opcionales)'}</span>
                </button>

                {showAdvanced && (
                    <div className="cme-addloc__advanced">
                        <section className="cme-addloc__section">
                            <h3 className="cme-addloc__section-title">Estadísticas de servicios</h3>
                            <div className="cme-addloc__row">
                                <div className="cme-addloc__field">
                                    <label className="cme-addloc__label" htmlFor="cme-addloc-svccat">
                                        Service Categories Count
                                    </label>
                                    <textarea
                                        id="cme-addloc-svccat"
                                        className="cme-addloc__textarea"
                                        value={location.serviceCategoriesCount}
                                        onChange={(e) => handleChange('serviceCategoriesCount', e.target.value)}
                                        placeholder='{"Plomería": 2, "Electricidad": 3}'
                                        rows={3}
                                    />
                                    <span className="cme-addloc__hint">Formato JSON: {'{"categoría": cantidad}'}</span>
                                </div>
                                <div className="cme-addloc__field">
                                    <label className="cme-addloc__label" htmlFor="cme-addloc-svcprov">Service Providers</label>
                                    <input
                                        id="cme-addloc-svcprov"
                                        type="number"
                                        className="cme-addloc__input"
                                        value={location.serviceProviders}
                                        onChange={(e) => handleChange('serviceProviders', e.target.value)}
                                        min="0"
                                    />
                                </div>
                            </div>
                        </section>

                        <section className="cme-addloc__section">
                            <h3 className="cme-addloc__section-title">Estadísticas de productos</h3>
                            <div className="cme-addloc__row">
                                <div className="cme-addloc__field">
                                    <label className="cme-addloc__label" htmlFor="cme-addloc-prodcat">
                                        Products Categories Count
                                    </label>
                                    <textarea
                                        id="cme-addloc-prodcat"
                                        className="cme-addloc__textarea"
                                        value={location.productsCategoriesCount}
                                        onChange={(e) => handleChange('productsCategoriesCount', e.target.value)}
                                        placeholder='{"Materiales": 1}'
                                        rows={3}
                                    />
                                    <span className="cme-addloc__hint">Formato JSON: {'{"categoría": cantidad}'}</span>
                                </div>
                                <div className="cme-addloc__field">
                                    <label className="cme-addloc__label" htmlFor="cme-addloc-prodprov">Products Providers</label>
                                    <input
                                        id="cme-addloc-prodprov"
                                        type="number"
                                        className="cme-addloc__input"
                                        value={location.productsProviders}
                                        onChange={(e) => handleChange('productsProviders', e.target.value)}
                                        min="0"
                                    />
                                </div>
                            </div>
                        </section>
                    </div>
                )}

                <div className="cme-addloc__actions">
                    <button
                        type="submit"
                        className="cme-addloc__submit-btn"
                        disabled={loading}
                    >
                        <Plus size={14} />
                        <span>{loading ? 'Creando…' : 'Crear location'}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddLocation;
